import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useState, useRef } from 'react';
import { useSpring, animated, config } from 'react-spring'
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// ─── CGPA Message Engine ───────────────────────────────────────────────────
const getCGPAFeedback = (cgpa) => {
  const val = parseFloat(cgpa);

  if (val >= 9.5) return {
    emoji: '👑',
    title: 'Absolute Legend.',
    message: "You didn't just pass — you rewrote the curve.",
    color: '#eecf1f',
    glow: '0 0 24px #FFD70066',
    badge: 'GOLD TIER'
  };
  if (val >= 9.0) return {
    emoji: '🔥',
    title: 'Outstanding Performance.',
    message: "Top percentile. Keep this energy and no door stays closed for long.",
    color: '#ef602c',
    glow: '0 0 24px #c44f2466',
    badge: 'ELITE'
  };
  if (val >= 8.5) return {
    emoji: '⚡',
    title: 'Impressive Work.',
    message: "Strong academics with real potential.",
    color: '#08bef5',
    glow: '0 0 24px #02c4ffe2',
    badge: 'HIGH ACHIEVER'
  };
  if (val >= 8.0) return {
    emoji: '🎯',
    title: 'Well Done.',
    message: "Solid performance. One more focused semester and you'll be untouchable.",
    color: '#1cf618',
    glow: '0 0 24px #1eea1b55',
    badge: 'ON TRACK'
  };
  if (val >= 7.0) return {
    emoji: '💪',
    title: 'Decent Start.',
    message: "You've got the foundation. Consistency is what separates good from great — push harder.",
    color: '#26e6a6',
    glow: '0 0 24px #10B98155',
    badge: 'KEEP GOING'
  };
  if (val >= 6.0) return {
    emoji: '📈',
    title: 'Room to Grow.',
    message: "The grades don't define you — yet. What you do next semester will. Make it count.",
    color: '#F59E0B',
    glow: '0 0 24px #F59E0B55',
    badge: 'BOUNCE BACK'
  };
  if (val >= 5.0) return {
    emoji: '⚠️',
    title: 'Time to Recalibrate.',
    message: "Let's be honest — this isn't your best. But it's not your last chance either. Regroup and move.",
    color: '#EF4444',
    glow: '0 0 24px #EF444455',
    badge: 'CRITICAL ZONE'
  };
  return {
    emoji: '🛠️',
    title: 'Reset. Rebuild. Return.',
    message: "This is the low point. The only direction from here is up — but only if you decide to move.",
    color: '#6B7280',
    glow: '0 0 20px #6B728055',
    badge: 'RECOVERY MODE'
  };
};

// ─── Percentage Conversion (Anna Univ formula) ─────────────────────────────
const toPercentage = (cgpa) => {
  const val = parseFloat(cgpa);
  if (isNaN(val)) return '—';
  return (val * 10).toFixed(2) + '%';
};

// ─── Main Modal ─────────────────────────────────────────────────────────────
export default function Modal({ isOpen, setIsOpen, toggleModal, result, gpas, credits, sems }) {
  const [flip, set] = useState(false);
  const [sharing, setSharing] = useState(false);
  const shareCardRef = useRef(null);
  const feedback = getCGPAFeedback(result);

  // ─── Generate & Share Image ─────────────────────────────────────────────
  const handleShareImage = async () => {
    if (!shareCardRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(shareCardRef.current, {
        backgroundColor: null,
        scale: 2, // retina quality
        useCORS: true,
      });

      canvas.toBlob(async (blob) => {
        const file = new File([blob], 'my-cgpa.png', { type: 'image/png' });

        // Try Web Share API (mobile-friendly)
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: `My CGPA — ${parseFloat(result).toFixed(2)}`,
            text: `Check out my CGPA calculated at sonacgpa.vercel.app`,
          });
        } else {
          // Fallback: download the image
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'my-cgpa.png';
          a.click();
          URL.revokeObjectURL(url);
        }
        setSharing(false);
      }, 'image/png');
    } catch (err) {
      console.error('Share failed:', err);
      setSharing(false);
    }
  };

  // ─── Copy / Share Text (mobile-safe) ───────────────────────────────────
  const handleCopy = async () => {
    const text = `My CGPA: ${parseFloat(result).toFixed(2)} (${toPercentage(result)}) ${feedback.emoji}\n${feedback.title}\nCalculated at sonacgpa.vercel.app\nCreated by Aravinth R`;

    // Mobile → native share sheet (WhatsApp, Messages, etc.)
    if (navigator.share) {
      try {
        await navigator.share({ text });
        return;
      } catch (err) {
        // User cancelled share — do nothing
        return;
      }
    }

    // Desktop → clipboard
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied! 📋');
    } catch {
      // Last resort fallback
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('Copied! 📋');
    }
  };

  // ─── Download PDF ─────────────────────────────────────────────────────
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - margin * 2;
    const headerY = 40;
    const tableStartY = 130;
    const rowHeight = 22;
    const columns = [
      { title: 'Semester', x: margin + 10, width: 80 },
      { title: 'GPA', x: margin + 110, width: 80 },
      { title: 'Credits', x: margin + 210, width: 80 }
    ];

    // Colors
    const primaryColor = [0, 102, 204];
    const secondaryColor = [34, 34, 34];
    const lightGray = [245, 245, 245];
    const dividerColor = [200, 200, 200];

    // Header Background
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 80, 'F');

    // Header Text
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('CGPA REPORT', pageWidth / 2, headerY, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Sona College of Technology', pageWidth / 2, headerY + 18, { align: 'center' });
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, headerY + 34, { align: 'center' });

    // Branch / Summary Title
    doc.setTextColor(...secondaryColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Name of the Branch : B.E. - COMPUTER SCIENCE AND ENGINEERING', margin, 105);

    // Table container
    doc.setDrawColor(...dividerColor);
    doc.setLineWidth(0.5);
    doc.rect(margin, tableStartY - 20, contentWidth, rowHeight * (Math.max(sems, 3) + 1) + 10, 'S');

    // Table header row
    doc.setFillColor(...primaryColor);
    doc.rect(margin, tableStartY - 20, contentWidth, rowHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    columns.forEach((column) => {
      doc.text(column.title, column.x, tableStartY - 3);
    });

    // Table rows
    let yPosition = tableStartY + rowHeight - 5;
    const semesterGpas = gpas || [];
    const semesterCredits = credits || [];

    doc.setTextColor(...secondaryColor);
    doc.setFont('helvetica', 'normal');

    for (let i = 0; i < sems; i++) {
      if (i % 2 === 0) {
        doc.setFillColor(...lightGray);
        doc.rect(margin, yPosition - rowHeight + 4, contentWidth, rowHeight, 'F');
      }

      doc.setDrawColor(...dividerColor);
      doc.line(margin, yPosition + 10, margin + contentWidth, yPosition + 10);

      doc.text(`Sem ${i + 1}`, columns[0].x, yPosition);
      doc.text((semesterGpas[i] || 0).toString(), columns[1].x, yPosition);
      doc.text((semesterCredits[i] || 0).toString(), columns[2].x, yPosition);

      yPosition += rowHeight;
    }

    // Final summary
    const finalBoxY = yPosition + 10;
    doc.setFillColor(...primaryColor);
    doc.roundedRect(margin, finalBoxY, contentWidth, 50, 6, 6, 'F');

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(`FINAL CGPA : ${parseFloat(result).toFixed(2)}`, margin + 20, finalBoxY + 20);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Equivalent Percentage : ${toPercentage(result)}`, margin + 20, finalBoxY + 38);

    // Footer text just below the final box
    const footerY = finalBoxY + 70;
    doc.setTextColor(...secondaryColor);
    doc.setFontSize(15);
    doc.text('Calculated at sonacgpa.vercel.app', pageWidth / 2, footerY, { align: 'center' });
    doc.text('Created by Aravinth R (CSE \'22-26)', pageWidth / 2, footerY + 14, { align: 'center' });

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'cgpa-report.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(pdfUrl);
  };

  const { number } = useSpring({
    reset: true,
    reverse: flip,
    from: { number: 0 },
    number: Number(result),
    delay: 200,
    config: config.molasses,
  });

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-10 overflow-y-auto" onClose={toggleModal}>
        <div className="min-h-screen px-4 text-center">

          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" />
          </Transition.Child>

          <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>

          {/* Modal Box */}
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <div
              className="inline-block w-full max-w-md p-6 my-8 text-left align-middle transition-all transform rounded-2xl overflow-hidden"
              style={{
                background: '#1a1b26',
                border: `2px solid ${feedback.color}`,
                boxShadow: feedback.glow,
              }}
            >
              {/* Badge */}
              <div className="flex justify-between items-center mb-4">
                <span
                  className="text-xs font-black tracking-widest px-3 py-1 rounded-full"
                  style={{ background: feedback.color + '22', color: feedback.color, border: `1px solid ${feedback.color}55` }}
                >
                  {feedback.badge}
                </span>
                <span className="text-2xl">{feedback.emoji}</span>
              </div>

              {/* Title */}
              <Dialog.Title className="text-xl font-black text-white mb-1">
                {feedback.title}
              </Dialog.Title>

              {/* Animated CGPA Number */}
              <animated.div
                className="text-5xl font-black text-center my-4"
                style={{ color: feedback.color, textShadow: feedback.glow }}
              >
                {number.to(n => n.toFixed(2))}
              </animated.div>

              {/* Percentage Row */}
              <div
                className="text-center text-sm font-semibold mb-4 py-1 rounded-lg"
                style={{ background: feedback.color + '15', color: feedback.color }}
              >
                ≈ {toPercentage(result)} &nbsp;|&nbsp; Sona College 
              </div>

              {/* Roast / Praise Message */}
              <p className="text-gray-300 text-sm leading-relaxed border-l-2 pl-3 mb-5"
                style={{ borderColor: feedback.color }}>
                {feedback.message}
              </p>

              {/* Actions */}
              <div className="flex gap-2 mt-2 flex-wrap">
                {/* Close */}
                <button
                  onClick={toggleModal}
                  className="flex-1 px-4 py-2 text-sm font-bold rounded-lg transition-all duration-200"
                  style={{
                    background: feedback.color + '20',
                    color: feedback.color,
                    border: `1px solid ${feedback.color}55`,
                  }}
                  onMouseEnter={e => e.target.style.background = feedback.color + '40'}
                  onMouseLeave={e => e.target.style.background = feedback.color + '20'}
                >
                  Got it!
                </button>

                {/* Copy Text */}
                {/* <button
                  onClick={handleCopy}
                  className="px-3 py-2 text-sm font-bold rounded-lg transition-all duration-200"
                  style={{ background: '#ffffff10', color: '#ffffff88', border: '1px solid #ffffff20' }}
                  onMouseEnter={e => e.target.style.background = '#ffffff25'}
                  onMouseLeave={e => e.target.style.background = '#ffffff10'}
                  title="Copy as text"
                >
                  📋Copy Text
                </button> */}

                {/* Download PDF */}
                <button
                  onClick={handleDownloadPDF}
                  className="px-3 py-2 text-sm font-bold rounded-lg transition-all duration-200"
                  style={{ background: '#ffffff10', color: '#ffffff88', border: '1px solid #ffffff20' }}
                  onMouseEnter={e => e.target.style.background = '#ffffff25'}
                  onMouseLeave={e => e.target.style.background = '#ffffff10'}
                  title="Download PDF"
                >
                  📄Download PDF
                </button>

                {/* Share as Image */}
                <button
                  onClick={handleShareImage}
                  disabled={sharing}
                  className="flex-1 px-4 py-2 text-sm font-bold rounded-lg transition-all duration-200"
                  style={{
                    background: sharing ? '#ffffff10' : feedback.color,
                    color: sharing ? '#ffffff55' : '#000',
                    border: 'none',
                    cursor: sharing ? 'not-allowed' : 'pointer',
                  }}
                >
                  {sharing ? 'Generating...' : '🖼️ Share Image'}
                </button>
              </div>

              {/* Created By */}
              <div className="text-center mt-3 font-bold" style={{ fontSize: '15px', letterSpacing: '1px', color: '#dadde7' }}>
                sonacgpa.vercel.app &nbsp;·&nbsp; Created by Aravinth R (CSE '22-26)
              </div>

            </div>
          </Transition.Child>

          {/* ─── Hidden Share Card (captured by html2canvas) ─────────────────── */}
          <div style={{ position: 'fixed', left: '-9999px', top: 0 }}>
            <div
              ref={shareCardRef}
              style={{
                width: '400px',
                padding: '32px',
                background: '#1a1b26',
                border: `2px solid ${feedback.color}`,
                boxShadow: feedback.glow,
                borderRadius: '20px',
                fontFamily: 'sans-serif',
              }}
            >
              {/* Card Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{
                  fontSize: '10px', fontWeight: 900, letterSpacing: '3px',
                  padding: '4px 10px', borderRadius: '999px',
                  background: feedback.color + '22', color: feedback.color,
                  border: `1px solid ${feedback.color}55`,
                }}>
                  {feedback.badge}
                </span>
                <span style={{ fontSize: '28px' }}>{feedback.emoji}</span>
              </div>

              {/* College Name */}
              <div style={{ fontSize: '11px', color: '#ffffff55', letterSpacing: '2px', marginBottom: '8px' }}>
                SONA COLLEGE OF TECHNOLOGY
              </div>

              {/* Title */}
              <div style={{ fontSize: '18px', fontWeight: 900, color: '#fff', marginBottom: '12px' }}>
                {feedback.title}
              </div>

              {/* CGPA */}
              <div style={{
                fontSize: '64px', fontWeight: 900, textAlign: 'center',
                color: feedback.color, textShadow: feedback.glow, margin: '12px 0'
              }}>
                {parseFloat(result).toFixed(2)}
              </div>

              {/* Percentage */}
              <div style={{
                textAlign: 'center', fontSize: '13px', fontWeight: 700,
                padding: '6px', borderRadius: '8px', marginBottom: '16px',
                background: feedback.color + '18', color: feedback.color,
              }}>
                ≈ {toPercentage(result)} &nbsp;|&nbsp; Sona College 
              </div>

              {/* Message */}
              <div style={{
                fontSize: '12px', color: '#cbd5e1', lineHeight: 1.6,
                borderLeft: `3px solid ${feedback.color}`, paddingLeft: '12px', marginBottom: '20px'
              }}>
                {feedback.message}
              </div>

              {/* Footer */}
              <div style={{ fontSize: '15px', color: '#eeeef2', textAlign: 'center', letterSpacing: '1px' , fontWeight: 'bold'}}>
                sonacgpa.vercel.app &nbsp;|&nbsp; Created by Aravinth R (CSE '22-26)
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}




// import { Dialog, Transition } from '@headlessui/react'
// import { Fragment, useState } from 'react';
// import { useSpring, animated, config } from 'react-spring'
 
// // ─── CGPA Message Engine ───────────────────────────────────────────────────
// const getCGPAFeedback = (cgpa) => {
//   const val = parseFloat(cgpa);
 
//   if (val >= 9.5) return {
//     emoji: '👑',
//     title: 'Absolute Legend.',
//     message: "You didn't just pass — you rewrote the curve. Recruiters will fight over you.",
//     color: '#FFD700',
//     glow: '0 0 24px #FFD70066',
//     badge: 'GOLD TIER'
//   };
//   if (val >= 9.0) return {
//     emoji: '🔥',
//     title: 'Outstanding Performance.',
//     message: "Top percentile. Keep this energy and no door stays closed for long.",
//     color: '#FF6B35',
//     glow: '0 0 24px #FF6B3566',
//     badge: 'ELITE'
//   };
//   if (val >= 8.5) return {
//     emoji: '⚡',
//     title: 'Impressive Work.',
//     message: "Strong academics with real potential. You're exactly who companies want to interview.",
//     color: '#00C2FF',
//     glow: '0 0 24px #00C2FF55',
//     badge: 'HIGH ACHIEVER'
//   };
//   if (val >= 8.0) return {
//     emoji: '🎯',
//     title: 'Well Done.',
//     message: "Solid performance. One more focused semester and you'll be untouchable.",
//     color: '#7C3AED',
//     glow: '0 0 24px #7C3AED55',
//     badge: 'ON TRACK'
//   };
//   if (val >= 7.0) return {
//     emoji: '💪',
//     title: 'Decent Start.',
//     message: "You've got the foundation. Consistency is what separates good from great — push harder.",
//     color: '#10B981',
//     glow: '0 0 24px #10B98155',
//     badge: 'KEEP GOING'
//   };
//   if (val >= 6.0) return {
//     emoji: '📈',
//     title: 'Room to Grow.',
//     message: "The grades don't define you — yet. What you do next semester will. Make it count.",
//     color: '#F59E0B',
//     glow: '0 0 24px #F59E0B55',
//     badge: 'BOUNCE BACK'
//   };
//   if (val >= 5.0) return {
//     emoji: '⚠️',
//     title: 'Time to Recalibrate.',
//     message: "Let's be honest — this isn't your best. But it's not your last chance either. Regroup and move.",
//     color: '#EF4444',
//     glow: '0 0 24px #EF444455',
//     badge: 'CRITICAL ZONE'
//   };
//   return {
//     emoji: '🛠️',
//     title: 'Reset. Rebuild. Return.',
//     message: "This is the low point. The only direction from here is up — but only if you decide to move.",
//     color: '#6B7280',
//     glow: '0 0 20px #6B728055',
//     badge: 'RECOVERY MODE'
//   };
// };
 
// // ─── Percentage Conversion (Anna Univ formula) ─────────────────────────────
// // const toPercentage = (cgpa) => {
// //   const val = parseFloat(cgpa);
// //   if (isNaN(val)) return '—';
// //   return ((val - 0.5) * 10).toFixed(2) + '%';
// // };

// const toPercentage = (cgpa) => {
//   const val = parseFloat(cgpa);
//   if (isNaN(val)) return '—';
//   return (val * 10).toFixed(2) + '%';  // Correct ✅
// };
 
// // ─── Main Modal ─────────────────────────────────────────────────────────────
// export default function Modal({ isOpen, setIsOpen, toggleModal, result }) {
//   const [flip, set] = useState(false);
//   const feedback = getCGPAFeedback(result);
 
//   const { number } = useSpring({
//     reset: true,
//     reverse: flip,
//     from: { number: 0 },
//     number: Number(result),
//     delay: 200,
//     config: config.molasses,
//   });
 
//   return (
//     <Transition appear show={isOpen} as={Fragment}>
//       <Dialog as="div" className="fixed inset-0 z-10 overflow-y-auto" onClose={toggleModal}>
//         <div className="min-h-screen px-4 text-center">
 
//           {/* Backdrop */}
//           <Transition.Child
//             as={Fragment}
//             enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
//             leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
//           >
//             <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" />
//           </Transition.Child>
 
//           <span className="inline-block h-screen align-middle" aria-hidden="true">&#8203;</span>
 
//           {/* Modal Box */}
//           <Transition.Child
//             as={Fragment}
//             enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
//             leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
//           >
//             <div
//               className="inline-block w-full max-w-md p-6 my-8 text-left align-middle transition-all transform rounded-2xl overflow-hidden"
//               style={{
//                 background: '#1a1b26',
//                 border: `2px solid ${feedback.color}`,
//                 boxShadow: feedback.glow,
//               }}
//             >
//               {/* Badge */}
//               <div className="flex justify-between items-center mb-4">
//                 <span
//                   className="text-xs font-black tracking-widest px-3 py-1 rounded-full"
//                   style={{ background: feedback.color + '22', color: feedback.color, border: `1px solid ${feedback.color}55` }}
//                 >
//                   {feedback.badge}
//                 </span>
//                 <span className="text-2xl">{feedback.emoji}</span>
//               </div>
 
//               {/* Title */}
//               <Dialog.Title className="text-xl font-black text-white mb-1">
//                 {feedback.title}
//               </Dialog.Title>
 
//               {/* Animated CGPA Number */}
//               <animated.div
//                 className="text-5xl font-black text-center my-4"
//                 style={{ color: feedback.color, textShadow: feedback.glow }}
//               >
//                 {number.to(n => n.toFixed(2))}
//               </animated.div>
 
//               {/* Percentage Row */}
//               <div
//                 className="text-center text-sm font-semibold mb-4 py-1 rounded-lg"
//                 style={{ background: feedback.color + '15', color: feedback.color }}
//               >
//                 ≈ {toPercentage(result)} &nbsp;|&nbsp; Sona College 
//               </div>
 
//               {/* Roast / Praise Message */}
//               <p className="text-gray-300 text-sm leading-relaxed border-l-2 pl-3 mb-5"
//                 style={{ borderColor: feedback.color }}>
//                 {feedback.message}
//               </p>
 
//               {/* Actions */}
//               <div className="flex justify-between items-center">
//                 <button
//                   onClick={toggleModal}
//                   className="px-4 py-2 text-sm font-bold rounded-lg transition-all duration-200"
//                   style={{
//                     background: feedback.color + '20',
//                     color: feedback.color,
//                     border: `1px solid ${feedback.color}55`,
//                   }}
//                   onMouseEnter={e => e.target.style.background = feedback.color + '40'}
//                   onMouseLeave={e => e.target.style.background = feedback.color + '20'}
//                 >
//                   Got it, thanks!
//                 </button>
 
//                 {/* Share Button */}
//                 <button
//                   onClick={() => {
//                     const text = `My CGPA: ${parseFloat(result).toFixed(2)} | ${feedback.title} ${feedback.emoji}\nCalculated at sonacgpa.vercel.app`;
//                     navigator.clipboard.writeText(text).then(() => alert('Copied to clipboard! Share it 🚀'));
//                   }}
//                   className="px-4 py-2 text-sm font-bold rounded-lg transition-all duration-200"
//                   style={{
//                     background: '#ffffff10',
//                     color: '#ffffff88',
//                     border: '1px solid #ffffff20',
//                   }}
//                   onMouseEnter={e => e.target.style.background = '#ffffff25'}
//                   onMouseLeave={e => e.target.style.background = '#ffffff10'}
//                 >
//                   📋 Share Result
//                 </button>
//               </div>
//             </div>
//           </Transition.Child>
//         </div>
//       </Dialog>
//     </Transition>
//   );
// }

// import { Dialog, Transition } from '@headlessui/react'
  // import { Fragment, useState } from 'react';
  // import { useSpring, animated, config } from 'react-spring'
  // import { Link } from 'react-router-dom';

  // export default function Modal({ isOpen, setIsOpen, toggleModal, result }) {
  //   const [flip, set] = useState(false)
  //   const { number } = useSpring({
  //     reset: true,
  //     reverse: flip,
  //     from: { number: 0 },
  //     number: Number(result),
  //     delay: 200,
  //     config: config.molasses,
  //   })

  //   return (
  //     <>
  //       <Transition appear show={isOpen} as={Fragment}>
  //         <Dialog
  //           as="div"
  //           className="fixed inset-0 z-10 overflow-y-auto"
  //           onClose={toggleModal}
  //         >
  //           <div className="min-h-screen px-4 text-center">
  //             <Transition.Child
  //               as={Fragment}
  //               enter="ease-out duration-300"
  //               enterFrom="opacity-0"
  //               enterTo="opacity-100"
  //               leave="ease-in duration-200"
  //               leaveFrom="opacity-100"
  //               leaveTo="opacity-0"
  //             >
  //               <Dialog.Overlay className="fixed inset-0" />
  //             </Transition.Child>

  //             {/* This element is to trick the browser into centering the modal contents. */}
  //             <span
  //               className="inline-block h-screen align-middle"
  //               aria-hidden="true"
  //             >
  //               &#8203;
  //             </span>
  //             <Transition.Child
  //               as={Fragment}
  //               enter="ease-out duration-300"
  //               enterFrom="opacity-0 scale-95"
  //               enterTo="opacity-100 scale-100"
  //               leave="ease-in duration-200"
  //               leaveFrom="opacity-100 scale-100"
  //               leaveTo="opacity-0 scale-95"
  //             >
  //               <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-[#21222D] border-4 border-[#D53580] shadow-xl rounded-2xl">
  //                 <Dialog.Title
  //                   as="h3"
  //                   className="text-lg font-medium leading-6 text-white"
  //                 >
  //                   Congratulations!!
  //                 </Dialog.Title>
  //                 <div className="mt-2">
  //                   <animated.div className="text-3xl font-bold text-white text-center">
  //                     {number.to(n => n.toFixed(2))}
  //                   </animated.div>
  //                 </div>

  //                 <div className="mt-4 flex justify-between">
  //                   <button
  //                     type="button"
  //                     className="inline-flex mr-1 justify-center px-4 py-2 text-sm font-bold text-[#0689FF] border-2 border-transparent rounded-md hover:border-[#0689FF] focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
  //                     onClick={toggleModal}
  //                   >
  //                     Got it, thanks!
  //                   </button>
  //                   {/* <Link 
  //                     to="/mailForm"
  //                     className="inline-flex ml-1 px-4 py-2 text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"  
  //                   >Get CGPA in mail</Link> */}
  //                 </div>
  //               </div>
  //             </Transition.Child>
  //           </div>
  //         </Dialog>
  //       </Transition>
  //     </>
  //   )
  // }
